import { FormControl, MenuItem, Select } from '@mui/material';
import { Button, Text } from '@shared/components';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { formatDollars } from 'clerk_common/stringification/numbers';
import clsx from 'clsx';
import { compact, isEmpty, max, mean, min } from 'lodash';
import { useEffect, useState } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { AggregateType, LaneHistoryRowProps } from '../types';
type Variances = {
  buy: string;
  sell: string;
  margin: string;
};

const AggregateHeaderCell = ({
  value,
  className,
}: {
  value?: string;
  className?: string;
}) => {
  return (
    <th className={clsx('text-left text-gray-500', className)}>
      <Text type="body-xs">{value || '--'}</Text>
    </th>
  );
};

const getMedian = (values: number[]): number => {
  const sorted = compact(values).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted[mid];
};

export const getMargin = (row: {
  buyRate?: number;
  sellRate?: number;
}): number | null => {
  if (row.buyRate && row.sellRate) {
    return ((row.sellRate - row.buyRate) / row.buyRate) * 100;
  }
  return null;
};

const getMargins = (rows: LaneHistoryRowProps[]): number[] => {
  return compact(rows.map(getMargin));
};

const getVariance = (values: number[]): string => {
  if (isEmpty(values)) return '--';

  const meaVal = mean(values);
  const variance =
    values.reduce((a, b) => a + (b - meaVal) ** 2, 0) / values.length;
  const cv = Math.sqrt(variance) / meaVal;

  return `${(100 * cv).toFixed(0)}%`;
};

const getVariances = (rows: LaneHistoryRowProps[]): Variances => {
  const margins = getMargins(rows);
  const buys = compact(rows.map((row) => row.buyRate));
  const sells = compact(rows.map((row) => row.sellRate));

  return {
    buy: getVariance(buys),
    sell: getVariance(sells),
    margin: getVariance(margins),
  };
};
export const AggregateRow = ({
  rows,
  onRemove,
  onAdd,
}: {
  rows: LaneHistoryRowProps[];
  onRemove?: () => void;
  onAdd?: () => void;
}) => {
  const [aggregateType, setAggregateType] = useState<AggregateType>(
    rows.length > 2 ? 'Median' : 'Mean'
  );
  const { track } = useAnalytics();
  const [buy, setBuy] = useState<number | undefined>(undefined);
  const [sell, setSell] = useState<number | undefined>(undefined);
  const [margin, setMargin] = useState<number | undefined>(undefined);

  const variances = getVariances(rows);
  useEffect(() => {
    const margins = getMargins(rows);
    const buys = compact(rows.map((row) => row.buyRate));
    const sells = compact(rows.map((row) => row.sellRate));
    switch (aggregateType) {
      case 'Median': {
        setBuy(getMedian(buys));
        setSell(getMedian(sells));
        setMargin(getMedian(margins));
        break;
      }
      case 'Mean': {
        setBuy(mean(buys));
        setSell(mean(sells));
        setMargin(mean(margins));
        break;
      }
      case 'Min': {
        setBuy(min(buys));
        setSell(min(sells));
        setMargin(min(margins));
        break;
      }
      case 'Max': {
        setBuy(max(buys));
        setSell(max(sells));
        setMargin(max(margins));
        break;
      }
    }
  }, [aggregateType, rows]);

  const getDisplayValue = (
    variance: string,
    isDollars: boolean,
    value?: number
  ) => {
    if (aggregateType === 'Variance') {
      return variance;
    }

    if (!value) return '--';

    if (isDollars) {
      return formatDollars(value);
    }

    return `${value.toFixed(0)}%`;
  };

  return (
    <tr>
      <th colSpan={2}>
        <FormControl fullWidth margin="dense" size="small" sx={{ m: 0 }}>
          <Select
            value={aggregateType}
            onChange={(e) => {
              track('Toggled Lane History Aggregate Type', {
                data: {
                  aggregateType: e.target.value,
                },
              });
              setAggregateType(e.target.value as AggregateType);
            }}
            size="small"
            variant="standard"
          >
            <MenuItem value="Median">
              <Text type="body-xs">Median</Text>
            </MenuItem>
            <MenuItem value="Mean">
              <Text type="body-xs">Mean</Text>
            </MenuItem>
            <MenuItem value="Min">
              <Text type="body-xs">Min</Text>
            </MenuItem>
            <MenuItem value="Max">
              <Text type="body-xs">Max</Text>
            </MenuItem>
            <MenuItem value="Variance">
              <Text type="body-xs">Variance</Text>
            </MenuItem>
          </Select>
        </FormControl>
      </th>
      <AggregateHeaderCell value={getDisplayValue(variances?.buy, true, buy)} />
      <AggregateHeaderCell
        value={getDisplayValue(variances?.sell, true, sell)}
      />
      <AggregateHeaderCell
        value={getDisplayValue(variances?.margin, false, margin)}
        className="text-center"
      />
      <th />
      <td>
        {onRemove && (
          <Button
            variant="tertiary"
            size="small"
            onPress={onRemove}
            icon={<LuMinus />}
            tooltip="Remove aggregate"
          />
        )}
      </td>
      <td>
        {onAdd && (
          <Button
            variant="tertiary"
            size="small"
            onPress={onAdd}
            icon={<LuPlus />}
            tooltip="Add aggregate"
          />
        )}
      </td>
    </tr>
  );
};
